import React, { useState } from 'react';
import {
  Button,
  Menu,
  MenuItem,
  Checkbox,
  ListItemIcon,
  ListItemText,
  List,
  ListItemButton,
  Collapse,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp, faCheck } from '@fortawesome/pro-light-svg-icons';
import classNames from 'classnames';

import './styles.scss';

export interface SelectOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
  className?: string;
}

export interface SelectGroup {
  label: string;
  icon?: React.ReactNode;
  className?: string;
  value: string;
  items: (SelectOption | SelectGroup)[];
}

export interface SelectProps {
  options: (SelectOption | SelectGroup)[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  disabled?: boolean;
  className?: string;
  multiSelect?: boolean;
  placeholder?: string;
  showCheckbox?: boolean;
  showSelectedIcon?: boolean;

  renderSelectedLabel?: (selected: SelectOption | SelectGroup | undefined) => string;
}

const isGroup = (item: SelectOption | SelectGroup): item is SelectGroup => 'items' in item;

const Select: React.FC<SelectProps> = ({
  options,
  selectedValues,
  onChange,
  disabled = false,
  className,
  multiSelect = false,
  placeholder = 'Select...',
  showCheckbox = true,
  showSelectedIcon = true,
  renderSelectedLabel,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [openedGroups, setOpenedGroups] = useState<Set<string>>(new Set());

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (!disabled) {
      setAnchorEl(event.currentTarget);
      setOpenedGroups(new Set());
    }
  };

  const handleClose = () => setAnchorEl(null);

  const handleOptionClick = (value: string) => {
    const updated = multiSelect
      ? selectedValues.includes(value)
        ? selectedValues.filter((v) => v !== value)
        : [...selectedValues, value]
      : [value];
    onChange(updated);
    if (!multiSelect) handleClose();
  };

  const handleGroupClick = (key: string) =>
    setOpenedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });

  const renderMenuItems = (items: (SelectOption | SelectGroup)[], level = 0) => {
    const renderGroup = (item: SelectGroup) => {
      return (
        <div key={item.value}>
          <ListItemButton
            onClick={() => handleGroupClick(item.value)}
            className={classNames('select-group', item.className, `option-level-${level}`)}
          >
            {item.icon && <ListItemIcon>{item.icon}</ListItemIcon>}
            <ListItemText primary={item.label} />
            <FontAwesomeIcon
              className="select-icon"
              icon={openedGroups.has(item.value) ? faChevronUp : faChevronDown}
            />
          </ListItemButton>
          <Collapse in={openedGroups.has(item.value)} timeout="auto" unmountOnExit>
            <List component="div">{renderMenuItems(item.items, level + 1)}</List>
          </Collapse>
        </div>
      );
    };

    const renderOption = (item: SelectOption) => (
      <MenuItem
        key={item.value}
        onClick={() => handleOptionClick(item.value)}
        className={classNames('select-option', item.className, `option-level-${level}`)}
      >
        {multiSelect && showCheckbox && (
          <Checkbox checked={selectedValues.includes(item.value)} className="select-checkbox" />
        )}
        {item.icon && <ListItemIcon>{item.icon}</ListItemIcon>}
        <ListItemText>{item.label}</ListItemText>
        {showSelectedIcon && (
          <ListItemIcon>
            {selectedValues.includes(item.value) && (
              <FontAwesomeIcon icon={faCheck} className="select-icon" />
            )}
          </ListItemIcon>
        )}
      </MenuItem>
    );

    return items.map((item) => (isGroup(item) ? renderGroup(item) : renderOption(item)));
  };

  const getSelectedItem = () => {
    const findInItems = (
      items: (SelectOption | SelectGroup)[],
    ): SelectOption | SelectGroup | undefined => {
      for (const item of items) {
        if (selectedValues.includes(item.value)) {
          return item;
        }
        if (isGroup(item)) {
          const found = findInItems(item.items);
          if (found) return found;
        }
      }
      return undefined;
    };

    return findInItems(options);
  };

  const getButtonLabel = () => {
    const selected = getSelectedItem();

    if (renderSelectedLabel) {
      return renderSelectedLabel(selected);
    }

    if (multiSelect && selectedValues.length > 1) {
      return `${selectedValues.length} selected`;
    }

    return selected ? selected.label : placeholder;
  };

  const shouldShowSelected = () => {
    return renderSelectedLabel && getSelectedItem();
  };

  return (
    <div className={classNames('select-container', className)}>
      <Button
        onClick={handleClick}
        aria-controls={open ? 'select-menu' : undefined}
        aria-haspopup="true"
        disableRipple
        disabled={disabled}
        className={classNames('select-btn', {
          'select-btn-open': open,
          'select-btn-selected': shouldShowSelected(),
          'select-btn-disabled': disabled,
        })}
        endIcon={
          <FontAwesomeIcon icon={open ? faChevronUp : faChevronDown} className="select-icon" />
        }
      >
        <span className="select-label">{getButtonLabel()}</span>
      </Button>
      <Menu
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        className="select-menu"
      >
        {renderMenuItems(options)}
      </Menu>
    </div>
  );
};

export default Select;
